import {ApiResource} from 'common/util/model-resource/api-resource';
import {Model} from 'common/util/model-resource/model-resource';
import {
  EventDefinition,
  PropertyDefinition,
  PropertyDefinitionIdentity,
  PropertyDefinitionWithEvents,
} from 'common/report/queries/app-api/data-definitions-types';
import {
  eventPropertyDefinitionsResourceClient,
  getPropertyDefinitionDetails,
} from 'common/report/queries/app-api/property-definitions-client';
import {bulkUpdate, update} from 'common/report/queries/app-api/property-definitions-client';

export class EventPropertyDefinitionsResource extends ApiResource<PropertyDefinition> {
  apiClient = eventPropertyDefinitionsResourceClient;

  async getEvents(propertyIdentity: PropertyDefinitionIdentity): Promise<EventDefinition[]> {
    // this doesn't work for Custom properties
    const existing = this.getOne(propertyIdentity.name).value as PropertyDefinitionWithEvents;
    if (existing && existing.events) {
      return existing.events;
    }

    const params = this.getBasePropertyParams(propertyIdentity);
    params.set(`includeEvents`, true);

    const response = await getPropertyDefinitionDetails(params);
    // the API sends down a mal-formed PropertyDefinition object when it's not found; Don't update our cache if that's the case

    // for properties, name is always the right key
    this.update({[response.name]: response});

    return response.events;
  }

  private getBasePropertyParams(propDefinition: PropertyDefinitionIdentity): Map<string, any> {
    const params: Map<string, any> = new Map();
    if (propDefinition.id) {
      params.set(`id`, propDefinition.id);
    } else if (propDefinition.name) {
      // No id means it doesn't exist in database yet, need all the unique-together bits for PATCH
      params.set(`name`, propDefinition.name);
    }
    if (propDefinition.dataGroupId) {
      params.set(`dataGroupId`, propDefinition.dataGroupId);
    }
    return params;
  }

  async patch(eventName: string, propertyDefUpdate: Partial<PropertyDefinition>): Promise<Model<PropertyDefinition>> {
    const updateResponse = await update(this.buildUpdatePayload(eventName, propertyDefUpdate));
    this.update({[eventName]: updateResponse});
    return this.getOne(eventName);
  }

  async bulkPatch(eventDefUpdates: Record<string, Partial<PropertyDefinition>>): Promise<void> {
    const apiPayload = Object.entries(eventDefUpdates).map(([eventName, eventDefUpdate]) => {
      return this.buildUpdatePayload(eventName, eventDefUpdate);
    });
    await bulkUpdate(apiPayload);

    this.update(eventDefUpdates);
  }

  buildUpdatePayload(eventName: string, eventDefUpdate: Partial<PropertyDefinition>) {
    const eventDef = this.getOne(eventName).value;

    if (!eventDef && eventDefUpdate.id === undefined && eventDefUpdate.name === undefined) {
      throw new Error(
        `Could not find event definition with name ${eventName} in the store. Provide name or id in update payload.`,
      );
    }

    return {
      ...(eventDef?.id ? {id: eventDef?.id} : {}),
      name: eventDef?.name,
      ...eventDefUpdate,
    };
  }
}
